import { createStore } from '@/common/pinia';

const SCHEMA_VERSION = 1;
const STORAGE_PREFIX = 'natureos:layer_prefs';

export type LayerPanel = 'airborne';

export interface PersistedLayerPrefs {
  schemaVersion: number;
  order: {
    airborne: Record<string, string[]>;
  };
  hidden: {
    airborne: string[];
  };
}

function defaultPrefs(): PersistedLayerPrefs {
  return {
    schemaVersion: SCHEMA_VERSION,
    order: { airborne: {} },
    hidden: { airborne: [] },
  };
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:v${SCHEMA_VERSION}:${userId}`;
}

class LayerCustomizationStore {
  public userId: string | null = null;
  public prefs: PersistedLayerPrefs = defaultPrefs();

  public load(userId: string | null): void {
    this.userId = userId;
    if (!userId) {
      this.prefs = defaultPrefs();
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey(userId));
      if (!raw) {
        this.prefs = defaultPrefs();
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.schemaVersion !== SCHEMA_VERSION) {
        this.prefs = defaultPrefs();
        return;
      }
      this.prefs = {
        schemaVersion: SCHEMA_VERSION,
        order: {
          airborne: parsed.order?.airborne ?? {},
        },
        hidden: {
          airborne: parsed.hidden?.airborne ?? [],
        },
      };
    } catch {
      this.prefs = defaultPrefs();
    }
  }

  public setOrder(panel: LayerPanel, category: string, layerNames: string[]): void {
    this.prefs.order[panel][category] = [...layerNames];
    this.persist();
  }

  public hide(panel: LayerPanel, layerName: string): void {
    if (this.prefs.hidden[panel].includes(layerName)) return;
    this.prefs.hidden[panel].push(layerName);
    this.persist();
  }

  public restore(panel: LayerPanel, layerName: string): void {
    const idx = this.prefs.hidden[panel].indexOf(layerName);
    if (idx === -1) return;
    this.prefs.hidden[panel].splice(idx, 1);
    this.persist();
  }

  public isHidden(panel: LayerPanel, layerName: string): boolean {
    return this.prefs.hidden[panel].includes(layerName);
  }

  public getOrder(panel: LayerPanel, category: string): string[] {
    return this.prefs.order[panel][category] ?? [];
  }

  public getHidden(panel: LayerPanel): string[] {
    return this.prefs.hidden[panel];
  }

  public totalHidden(): number {
    return this.prefs.hidden.airborne.length;
  }

  public clear(): void {
    if (this.userId) {
      localStorage.removeItem(storageKey(this.userId));
    }
    this.prefs = defaultPrefs();
  }

  private persist(): void {
    if (!this.userId) return;
    try {
      localStorage.setItem(storageKey(this.userId), JSON.stringify(this.prefs));
    } catch {
      // quota or serialization errors are non-fatal; state stays in memory
    }
  }
}

const useLayerCustomizationStore = createStore(LayerCustomizationStore);

export { useLayerCustomizationStore, type LayerCustomizationStore };
